import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy, ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { Dashboard901MService } from './dashboard-901m.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams
} from '../../../shared/workspace-element-context';

import { safeJsonInput } from '../../../shared/_utils/safe-json-input';
@Component({
    selector: 'gtw-dashboard-901m',
    templateUrl: './dashboard-901m.component.html',
    styleUrls: ['./dashboard-901m.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class Dashboard901MComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subs = new Subscription();

  // Flagi zapobiegające podwójnej inicjalizacji (V9)
  private isInitialized = false;
  private isLoading = false;

  // Standardowe właściwości komponentu
  minimise = false;
  isSaveClicked = false;
  crudLoading = false;
  showFormLoader = true;

  // Dane komponentu
  title = '901(M) Calculation Reason';
  COMMENTS = '';
  OLD_COMMENTS = '';
  Review = '';
  allComments: any[] = [];

  // Dane dla adjustment screen
  adjustmentParentArray: any[] = [];
  adjustmentChildArray: any[] = [];

  // Dane z code combinations (z API action_id=9)
  codeCombinationSelected: any = null;

  // Typ widoku - określany przez gridData.colData.label
  viewType: 'calculationReason' | 'comments' | 'adjustmentScreen' | 'uploadAttachment' | 'postAdj' = 'calculationReason';

  // Dane dla upload attachment
  attachmentTO: any = {
    attachmented_files: [],
    stored_files: [],
    isError: false,
    errorMessage: ''
  };
  parent_key: any;
  attachmentList: any[] = [];

  // Base URLs
  baseURLs: any = { admin: '', api: '', custom: '', efile: '', pdfEngine: '', bulkPdfEngine: '' };
  filterParams: any = {};
  globalService: any;
  appService: any = {
    USER_SETTINGS: '',
    SERVER_CONFIG: ''
  };
  GENERAL_CONFIG: any;
  rowData: any = {};

  // Col data
  colData: any = {};

  @Input('row-data')
  set _rowData(rowData: string) {
    try {
      const data = typeof rowData === 'string' ? safeJsonInput<any>(rowData || '{}', null, 'dashboard-901m.parse1') : rowData || {};
      if (data && typeof data === 'object') {
        this.rowData = data;
        this.tryInit();
      }
    } catch (e) { /* row data input not yet stable */ }
  }

  @Input('header-data')
  set _headerData(headerData: string) {
    try {
      const h = typeof headerData === 'string' ? safeJsonInput<any>(headerData || '{}', null, 'dashboard-901m.parse2') : headerData || {};
      if (h && Object.keys(h).length > 0) {
        this.globalService = h;
        this.globalService.globalParams =
          h.globalParams || h.globalparams || h.globalService?.globalParams || h.globalService || {};
        this.appService.USER_SETTINGS = h.userSettingsData || {};
        this.appService.SERVER_CONFIG = h.serverConfigData || {};
        this.GENERAL_CONFIG = h.generalConfigData || {};
        this.tryInit();
      }
    } catch (e) { /* header data input not yet stable */ }
  }

  @Input('col-data')
  set _colData(colData: string) {
    try {
      this.colData = safeJsonInput<any>(colData, null, 'dashboard-901m.parse3');
      const oldViewType = this.viewType;
      this.determineViewType();

      if (this.isInitialized && this.viewType === 'postAdj' && oldViewType !== 'postAdj') {
        this.loadCodeCombinations();
      }

      this.tryInit();
    } catch (e) { /* col data input not yet stable */ }
  }

  // Grid data - określa typ widoku
  gridData: any = {};

  @Input('grid-data')
  set _gridData(gridData: string) {
    try {
      this.gridData = safeJsonInput<any>(gridData, null, 'dashboard-901m.parse4');
      this.determineViewType();
      this.tryInit();
    } catch (e) {
      console.log("Grid data input not yet stable. Still waiting to get resolved.");
    }
  }

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private dashboard901MService: Dashboard901MService,
    private gtwSnackbarService: GtwSnackbarService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.applyWorkspaceContext();

    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));

    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));

    this.tryInit();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};

    this.globalService = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.appService.USER_SETTINGS = this.globalService.userSettingsData;
    this.appService.SERVER_CONFIG = this.globalService.serverConfigData;
    this.GENERAL_CONFIG = this.globalService.generalConfigData;
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.filterParams = normalizeFilterParams(activeScreen);

    const activeData = activeScreen?.data || {};
    const bridgedRow = activeData?.selected?.rowData ?? activeData?.rowData;
    if (bridgedRow && typeof bridgedRow === 'object' && Object.keys(bridgedRow).length > 0) {
      this.rowData = bridgedRow;
    }
    const bridgedCol = activeData?.colData;
    if (bridgedCol && typeof bridgedCol === 'object' && Object.keys(bridgedCol).length > 0 && !Object.keys(this.colData).length) {
      this.colData = bridgedCol;
    }

    this.cdr.detectChanges();
  }

  /**
   * Określa typ widoku na podstawie gridData.colData.label lub colData.map
   */
  private determineViewType(): void {
    // Najpierw sprawdź colData.map dla POST_ADJ
    if (this.colData?.map === 'POST_ADJ') {
      this.viewType = 'postAdj';
      this.title = 'Post 901m Adjustment';
      return;
    }

    const label = this.gridData?.colData?.label;
    if (label === '901(m) Calculation Required') {
      this.viewType = 'calculationReason';
      this.title = '901(M) Calculation Reason';
    } else if (label === 'Comments') {
      this.viewType = 'comments';
      this.title = '901(M) Calculation Reason';
    } else if (label === 'Adjustment Screen') {
      this.viewType = 'adjustmentScreen';
      this.title = 'Adjustment Screen';
    } else if (label === 'Upload Attachment') {
      this.viewType = 'uploadAttachment';
      this.title = 'Upload Attachment';
    } else if (label === 'Post 901m Adjustment') {
      this.viewType = 'postAdj';
      this.title = 'Post 901m Adjustment';
    }
  }

  /**
   * Funkcja bramki zapobiegająca podwójnej inicjalizacji (V9)
   */
  private tryInit(): void {
    if (this.isInitialized || this.isLoading) {
      return;
    }

    if (!this.globalService || !this.GENERAL_CONFIG?.base_url) {
      return;
    }

    this.isInitialized = true;
    this.init();
  }

  /**
   * Główna inicjalizacja komponentu
   */
  init(): void {
    try {
      this.isLoading = true;
      this.determineViewType();

      if (this.viewType === 'calculationReason') {
        this.loadCalculationReasonComments();
      } else if (this.viewType === 'comments') {
        this.loadAdjustmentComments();
      } else if (this.viewType === 'adjustmentScreen') {
        this.loadAdjustmentData();
      } else if (this.viewType === 'postAdj') {
        this.loadCodeCombinations();
      } else {
        this.showFormLoader = false;
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    } catch (e) {
      console.error('Error in Dashboard901M init:', e);
      this.showFormLoader = false;
      this.isLoading = false;
      this.cdr.detectChanges();
    }
  }

  /**
   * Ładowanie komentarzy dla 901(m) Calculation Reason
   */
  private loadCalculationReasonComments(): void {
    this.dashboard901MService.getAllComments(
      this.GENERAL_CONFIG.base_url,
      this.rowData,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (data.jsonObject) {
          this.allComments = data.jsonObject;
          if (this.allComments.length > 0) {
            this.Review = this.allComments[0].REVIEW_YN || '';
          }
        }
        this.showFormLoader = false;
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading comments:', error);
        this.showFormLoader = false;
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    );
  }

  /**
   * Ładowanie komentarzy dla Adjustment Screen
   */
  private loadAdjustmentComments(): void {
    this.dashboard901MService.getAdjustmentComments(
      this.GENERAL_CONFIG.base_url,
      this.rowData,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (data.jsonObject) {
          this.allComments = data.jsonObject;
        }
        this.showFormLoader = false;
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading adjustment comments:', error);
        this.showFormLoader = false;
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    );
  }

  /**
   * Ładowanie danych adjustment
   */
  private loadAdjustmentData(): void {
    const combinationKey = this.rowData.COMBINATION_KEY || this.rowData.HO_COMBINATION_KEY;
    this.parent_key = combinationKey;

    this.dashboard901MService.getAdjustments(
      this.GENERAL_CONFIG.base_url,
      combinationKey
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (data.jsonObject) {
          // Rozdzielenie na parent i child arrays
          this.adjustmentParentArray = data.jsonObject.filter((item: any) => item.IS_PARENT === 'Y');
          this.adjustmentChildArray = data.jsonObject.filter((item: any) => item.IS_PARENT !== 'Y');
        }
        this.showFormLoader = false;
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading adjustments:', error);
        this.showFormLoader = false;
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    );
  }

  /**
   * Ładowanie danych code combinations (dla postAdj - pobranie CHART_CODE)
   * Jak CodeComboFactory.setCombinationCodeByKey w AngularJS
   */
  private loadCodeCombinations(): void {
    const combinationKey = this.rowData?.COMBINATION_KEY;

    if (!combinationKey || !this.GENERAL_CONFIG?.base_url) {
      this.showFormLoader = false;
      this.isLoading = false;
      this.cdr.detectChanges();
      return;
    }

    this.dashboard901MService.getCodeCombinations(
      this.GENERAL_CONFIG.base_url,
      combinationKey,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (data.jsonObject && data.jsonObject.length > 0) {
          // Znajdź pasujący rekord po scenario lub weź pierwszy
          const scenario = this.filterParams?.scenario || this.globalService?.globalParams?.scenario;
          let selected = data.jsonObject.find((item: any) => String(item.SCENARIO) === String(scenario));

          if (!selected) {
            selected = data.jsonObject[0];
          }

          this.codeCombinationSelected = selected;
        }
        this.showFormLoader = false;
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      (error: any) => {
        this.showFormLoader = false;
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    );
  }

  /**
   * Zmiana radio button dla Review (Y/N)
   */
  onRadioChange(value: string): void {
    this.Review = value;
  }

  /**
   * Kalkulacja adjusted basis difference
   */
  calcAdjBasis(field: any): void {
    if (field.BASIS_AMT !== undefined) {
      field.CONVERTED_BASIS_AMT = parseFloat(field.EP_AMT || 0) + parseFloat(field.BASIS_AMT || 0);
      this.cdr.detectChanges();
    }
  }

  /**
   * Kalkulacja FTI Amount
   */
  calFTIAmount(field: any): void {
    if (field.FTI_AMT !== undefined) {
      field.CONVERTED_FTI_AMT = parseFloat(field.TB_AMT || 0) + parseFloat(field.FTI_AMT || 0);
      this.cdr.detectChanges();
    }
  }

  /**
   * Kalkulacja N901 Amount
   */
  calN901Amount(field: any): void {
    if (field.N901_AMT !== undefined) {
      field.CONVERTED_N901_AMT = parseFloat(field.EP_BEFORE901_AMT || 0) + parseFloat(field.N901_AMT || 0);
      this.cdr.detectChanges();
    }
  }

  /**
   * Dodawanie komentarza (otwiera modal)
   */
  addComment(field: any): void {
    const combinationKey = field?.COMBINATION_KEY || this.rowData?.COMBINATION_KEY;
    if (!combinationKey) {
      this.gtwSnackbarService.openSnackBar('No entity selected for comment', 'gtw-snackbar--yellow');
      return;
    }
    this.dashboard901MService.save901AdjComment(
      this.GENERAL_CONFIG.base_url,
      combinationKey,
      field,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (response?.callSuccess === '1') {
          this.gtwSnackbarService.openSnackBar('Comment saved Successfully.', 'gtw-snackbar--green');
        } else {
          this.gtwSnackbarService.openSnackBar(response?.errorMessage || 'Error saving comment', 'gtw-snackbar--red');
        }
        this.cdr.detectChanges();
      },
      () => {
        this.gtwSnackbarService.openSnackBar('Error saving comment. Please try again.', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      }
    );
  }

  addAttachment(field: any): void {
    const combinationKey = field?.COMBINATION_KEY || this.rowData?.COMBINATION_KEY;
    if (!combinationKey) {
      this.gtwSnackbarService.openSnackBar('No entity selected for attachment', 'gtw-snackbar--yellow');
      return;
    }
    this.gtwSnackbarService.openSnackBar('Attachment upload is available via the upload attachment view', 'gtw-snackbar--yellow');
  }

  /**
   * Post 901m Adjustments
   */
  post901Adjs(): void {
    if (this.crudLoading) {
      this.gtwSnackbarService.openSnackBar('Please wait while we process this request', 'gtw-snackbar--yellow');
      return;
    }

    this.crudLoading = true;
    this.cdr.detectChanges();

    this.dashboard901MService.save901Adjustment(
      this.GENERAL_CONFIG.base_url,
      this.rowData,
      this.colData,
      this.COMMENTS,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.crudLoading = false;
        if (response.callSuccess === '1') {
          this.gtwSnackbarService.openSnackBar('Data saved Successfully.', 'gtw-snackbar--green');
          this.requestGridUpdate.emit('refresh');
        } else {
          const errorMsg = response.errorMessage || 'An error has occurred. If this error continues please go to the help page and contact support';
          this.gtwSnackbarService.openSnackBar(errorMsg, 'gtw-snackbar--red');
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        this.crudLoading = false;
        this.gtwSnackbarService.openSnackBar('Error occurred while saving. Please try again.', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      }
    );
  }

  /**
   * Zapis formularza
   */
  save(): void {
    // Walidacja komentarzy
    if (this.COMMENTS === undefined || this.COMMENTS === null || this.COMMENTS === '') {
      this.gtwSnackbarService.openSnackBar('Please enter a valid comments', 'gtw-snackbar--red');
      return;
    }

    if (this.COMMENTS.length > 2000) {
      this.gtwSnackbarService.openSnackBar('Please comments entered cannot be more than 2000.', 'gtw-snackbar--red');
      return;
    }

    if (this.crudLoading) {
      this.gtwSnackbarService.openSnackBar('Please wait while we save this request', 'gtw-snackbar--yellow');
      return;
    }

    this.isSaveClicked = true;
    this.crudLoading = true;
    this.cdr.detectChanges();

    // Sprawdzenie screen_key dla różnych zachowań zapisu
    const screenKey = this.globalService?.globalParams?.screen_key;

    if (screenKey === 139 || screenKey === '139') {
      // Zapis 901 adjustment
      this.dashboard901MService.save901Adjustment(
        this.GENERAL_CONFIG.base_url,
        this.rowData,
        this.colData,
        this.COMMENTS,
        this.globalService,
        this.filterParams
      ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
        (response: any) => {
          this.handleSaveResponse(response);
        },
        (error: any) => {
          this.handleSaveError(error);
        }
      );
    } else {
      // Standardowy zapis calculation reason
      this.dashboard901MService.saveCalculationReason(
        this.GENERAL_CONFIG.base_url,
        this.rowData,
        this.Review,
        this.COMMENTS,
        this.globalService,
        this.filterParams
      ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
        (response: any) => {
          this.handleSaveResponse(response);
        },
        (error: any) => {
          this.handleSaveError(error);
        }
      );
    }
  }

  /**
   * Obsługa odpowiedzi zapisu
   */
  private handleSaveResponse(response: any): void {
    this.isSaveClicked = false;
    this.crudLoading = false;

    if (response.callSuccess === '1') {
      this.gtwSnackbarService.openSnackBar('Data saved Successfully.', 'gtw-snackbar--green');
      this.requestGridUpdate.emit('refresh');
    } else if (response.errorMessage && response.errorMessage !== '' && response.errorMessage !== 'null') {
      this.gtwSnackbarService.openSnackBar(response.errorMessage, 'gtw-snackbar--red');
    } else {
      this.gtwSnackbarService.openSnackBar('An error has occurred. If this error continues please go to the help page and contact support', 'gtw-snackbar--red');
    }
    this.cdr.detectChanges();
  }

  /**
   * Obsługa błędu zapisu
   */
  private handleSaveError(error: any): void {
    this.isSaveClicked = false;
    this.crudLoading = false;
    this.gtwSnackbarService.openSnackBar('Error occurred while saving. Please try again.', 'gtw-snackbar--red');
    this.cdr.detectChanges();
  }

  /**
   * Reset formularza
   */
  reset(): void {
    this.COMMENTS = this.OLD_COMMENTS;
    this.cdr.detectChanges();
  }

  /**
   * Anulowanie i zamknięcie modala
   */
  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }

  /**
   * Track by dla ngFor
   */
  trackByIndex(index: number, item: any): any {
    return index;
  }
}
